import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Alice from "../../../assets/Screens/Characters/Alice.png";
import BabyCash from "../../../assets/Screens/Characters/BabyCash.png";
import CringeTeammate from "../../../assets/Screens/Characters/CringeTeammate.png";
import DominicFromTorento from "../../../assets/Screens/Characters/DominicFromTorento.png";
import FieryMentor from "../../../assets/Screens/Characters/FieryMentor.png";
import GitlWithABob from "../../../assets/Screens/Characters/GitlWithABob.png";
import GoldenGuy from "../../../assets/Screens/Characters/GoldenGuy.png";
import GrandmaZina from "../../../assets/Screens/Characters/GrandmaZina.png";
import Mark from "../../../assets/Screens/Characters/Mark.png";
import MishaTheHelper from "../../../assets/Screens/Characters/MishaTheHelper.png";
import MothersFriendsSon from "../../../assets/Screens/Characters/MothersFriendsSon.png";
import PlayboyUnderCover from "../../../assets/Screens/Characters/PlayboyUnderCover.png";
import Ravenna from "../../../assets/Screens/Characters/Ravenna.png";
import ZhenkaChan from "../../../assets/Screens/Characters/ZhenkaChan.png";

const CARD_WIDTH_PX = 225;
const SWIPE_MS = 800;
const INTERVAL_MS = 5000;

export default function CharactersCarousel() {
  const images = useMemo(
    () => [
      Alice,
      CringeTeammate,
      BabyCash,
      Ravenna,
      FieryMentor,
      GitlWithABob,
      GoldenGuy,
      Mark,
      MishaTheHelper,
      PlayboyUnderCover,
      ZhenkaChan,
      MothersFriendsSon,
      GrandmaZina,
      DominicFromTorento,
    ],
    []
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const commitAdvance = useCallback(
    (delta) => {
      setCurrentIndex((idx) => {
        const len = images.length;
        const next = idx + (delta > 0 ? 1 : -1);
        return ((next % len) + len) % len;
      });
      setProgress(0);
    },
    [images.length]
  );

  const startAdvance = useCallback(
    (delta) => {
      setProgress(delta > 0 ? 1 : -1);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        commitAdvance(delta);
      }, SWIPE_MS);
    },
    [commitAdvance]
  );

  const VISIBLE_COUNT = useMemo(() => {
    if (windowWidth >= 1792) return 9; // 3xl
    if (windowWidth >= 1280) return 7; // xl
    if (windowWidth >= 768) return 5; // lg
    return 3; // default
  }, [windowWidth]);

  const HALF_VISIBLE = Math.floor(VISIBLE_COUNT / 2);

  useEffect(() => {
    intervalRef.current = setInterval(() => startAdvance(1), INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [images.length, startAdvance]);

  useEffect(() => {
    if (images.length === 0) return;
    const len = images.length;
    const rightIndex = (((currentIndex + HALF_VISIBLE + 1) % len) + len) % len;
    const leftIndex = (((currentIndex - HALF_VISIBLE - 1) % len) + len) % len;
    const imgR = new Image();
    imgR.src = images[rightIndex];
    const imgL = new Image();
    imgL.src = images[leftIndex];
  }, [currentIndex, images, HALF_VISIBLE]);

  function at(idx) {
    const len = images.length;
    return ((idx % len) + len) % len;
  }

  const MIN_MARGIN_PERCENT = 5;
  const STEP_PERCENT = useMemo(
    () => (100 - MIN_MARGIN_PERCENT * 2) / (VISIBLE_COUNT - 1),
    [VISIBLE_COUNT]
  );

  useEffect(() => {
    const updateWindowWidth = () => {
      setWindowWidth(window.innerWidth);
    };

    updateWindowWidth();
    window.addEventListener("resize", updateWindowWidth);
    return () => window.removeEventListener("resize", updateWindowWidth);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const SLOT_COUNT = useMemo(() => VISIBLE_COUNT + 2, [VISIBLE_COUNT]);

  const EXTRA_SHIFT = 1;
  const centerSlot = HALF_VISIBLE + EXTRA_SHIFT + progress;
  const fracScales = useMemo(
    () =>
      Array.from({ length: SLOT_COUNT }, (_, i) => {
        const distanceToCenter = Math.min(
          Math.abs(i - centerSlot),
          HALF_VISIBLE
        );
        return 1 - (HALF_VISIBLE === 1 ? 0.2 : 0.1) * distanceToCenter;
      }),
    [SLOT_COUNT, centerSlot, HALF_VISIBLE]
  );

  const computeLayoutWidthsBase = useMemo(() => {
    return Array.from({ length: SLOT_COUNT + 1 }, (_, i) => {
      const distanceToCenter = Math.min(
        Math.abs(i - HALF_VISIBLE),
        HALF_VISIBLE
      );
      return (
        CARD_WIDTH_PX *
        (1 - (HALF_VISIBLE === 1 ? 0.2 : 0.1) * distanceToCenter)
      );
    });
  }, [SLOT_COUNT, HALF_VISIBLE]);

  function centerAtS(s, widthsBase, anchors) {
    const { c0, c3, c6 } = anchors;

    if (s <= HALF_VISIBLE) {
      const k = Math.max(0, Math.min(Math.floor(s), HALF_VISIBLE - 1));
      const t = s - k;
      let leftSpan = 0;
      for (let i = 0; i < HALF_VISIBLE; i++) {
        leftSpan += (widthsBase[i] + widthsBase[i + 1]) / 2;
      }

      const gL = (c3 - c0 - leftSpan) / HALF_VISIBLE;
      let cumulative = 0;
      for (let i = 0; i < k; i++) {
        cumulative += (widthsBase[i] + widthsBase[i + 1]) / 2 + gL;
      }
      const seg = (widthsBase[k] + widthsBase[k + 1]) / 2 + gL;
      const d = cumulative + t * seg;
      const totalLeft = leftSpan + HALF_VISIBLE * gL;
      const ratio = totalLeft > 0 ? d / totalLeft : 0;
      return c0 + ratio * (c3 - c0);
    }

    const sR = s - HALF_VISIBLE;
    const k = Math.max(0, Math.min(Math.floor(sR), HALF_VISIBLE - 1));
    const t = sR - k;
    let rightSpan = 0;
    for (let i = HALF_VISIBLE; i < HALF_VISIBLE + HALF_VISIBLE; i++) {
      rightSpan += (widthsBase[i] + widthsBase[i + 1]) / 2;
    }
    const gR = (c6 - c3 - rightSpan) / HALF_VISIBLE;
    let cumulative = 0;
    for (let i = 0; i < k; i++) {
      const idx = HALF_VISIBLE + i;
      cumulative += (widthsBase[idx] + widthsBase[idx + 1]) / 2 + gR;
    }
    const seg =
      (widthsBase[HALF_VISIBLE + k] + widthsBase[HALF_VISIBLE + k + 1]) / 2 +
      gR;
    const d = cumulative + t * seg;
    const totalRight = rightSpan + HALF_VISIBLE * gR;
    const ratio = totalRight > 0 ? d / totalRight : 0;
    return c3 + ratio * (c6 - c3);
  }

  let leftPercents = null;
  if (containerWidth > 0) {
    const c0 = (MIN_MARGIN_PERCENT / 100) * containerWidth;
    const c3 = 0.5 * containerWidth;
    const c6 = (1 - MIN_MARGIN_PERCENT / 100) * containerWidth;
    const anchors = { c0, c3, c6 };

    const widthsBase = computeLayoutWidthsBase;

    const centers = Array.from({ length: SLOT_COUNT }, (_, i) => {
      const s = i - progress - EXTRA_SHIFT;
      return centerAtS(s, widthsBase, anchors);
    });

    leftPercents = centers;
  }

  const handleClick = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const delta = x > rect.width / 2 ? 1 : -1;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (progress !== 0) {
      commitAdvance(progress > 0 ? 1 : -1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          startAdvance(delta);
        });
      });
    } else {
      startAdvance(delta);
    }

    intervalRef.current = setInterval(() => startAdvance(1), INTERVAL_MS);
  };

  return (
    <div className="relative w-full min-h-[475px] overflow-hidden">
      <div
        ref={containerRef}
        className="absolute inset-0"
        onClick={handleClick}
      >
        {Array.from({ length: SLOT_COUNT }).map((_, i) => {
          const relative = i - HALF_VISIBLE - EXTRA_SHIFT;
          const imageIndex = at(currentIndex + relative);
          const distanceToCenter = Math.min(
            Math.abs(i - centerSlot),
            HALF_VISIBLE
          );

          const scale = fracScales[i];
          const overlayOpacity =
            (HALF_VISIBLE === 1 ? 0.25 : 0.5) *
            (distanceToCenter / HALF_VISIBLE);

          const leftPercent =
            leftPercents !== null
              ? leftPercents[i]
              : ((MIN_MARGIN_PERCENT +
                  (i - progress - EXTRA_SHIFT) * STEP_PERCENT) /
                  100) *
                containerWidth;

          return (
            <div
              key={`carousel-item-${i}-${imageIndex}`}
              className="absolute top-1/2 -translate-y-1/2 transition-all duration-700 md:duration-1000 ease-out"
              style={{
                left: `50%`,
                transform: `translate(-50%, -50%) translateX(${leftPercent - containerWidth / 2}px)`,
                zIndex: Math.max(
                  0,
                  10 - Math.round((distanceToCenter / HALF_VISIBLE) * 10)
                ),
              }}
            >
              <div
                className="relative h-[475px] w-[225px] select-none transition-transform duration-700 md:duration-1000 ease-out"
                style={{ transform: `scale(${scale})` }}
              >
                <img
                  src={images[imageIndex]}
                  alt="Character"
                  className="h-full w-full object-contain drop-shadow-xl"
                  draggable={false}
                />
                <div
                  className="absolute inset-0 pointer-events-none rounded-[32px] bg-white transition-opacity duration-700 md:duration-1000 ease-out"
                  style={{
                    opacity: overlayOpacity,
                    WebkitMaskImage: `url(${images[imageIndex]})`,
                    maskImage: `url(${images[imageIndex]})`,
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
